import { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { getAllBookingsByUserId } from "../../State/Booking/Action";
import moment from "moment";
import UpdateBookingDialog from "./UpdateBookingDialog";
import CancellationModal from "../Tools/CancellationModal";
import { Card, CardContent, Typography, Chip, Button } from "@mui/material";
import DirectionsCarIcon from "@mui/icons-material/DirectionsCar";
import { motion } from "framer-motion";
import { HiCalendar, HiCurrencyRupee, HiIdentification, HiCog } from "react-icons/hi2";

export default function MyBookings() {
  const dispatch = useDispatch();
  const { booking } = useSelector((store) => store);

  useEffect(() => {
    dispatch(getAllBookingsByUserId());
  }, [dispatch]);

  const getStatusStyles = (status) => {
    switch (status) {
      case "BOOKED":
        return { bg: "bg-primary-100", text: "text-primary-700", border: "border-primary-200" };
      case "COMPLETED":
        return { bg: "bg-green-100", text: "text-green-700", border: "border-green-200" };
      case "CANCELLED":
        return { bg: "bg-red-100", text: "text-red-700", border: "border-red-200" };
      case "PENDING":
        return { bg: "bg-yellow-100", text: "text-yellow-700", border: "border-yellow-200" };
      default:
        return { bg: "bg-gray-100", text: "text-gray-700", border: "border-gray-200" };
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case "BOOKED":
        return "📅";
      case "COMPLETED":
        return "✅";
      case "CANCELLED":
        return "❌";
      case "PENDING":
        return "⏳";
      default:
        return "📋";
    }
  };

  return (
    <div className="min-h-screen gradient-bg pt-20 pb-20">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header Section */}
        <motion.div
          initial={{ opacity: 0, y: -30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          className="text-center mb-12"
        >
          <motion.div
            initial={{ scale: 0.8 }}
            animate={{ scale: 1 }}
            transition={{ delay: 0.2, duration: 0.6 }}
            className="inline-flex items-center gap-2 bg-white/80 backdrop-blur-sm rounded-full px-6 py-3 shadow-soft mb-6"
          >
            <HiCalendar className="w-5 h-5 text-primary-500" />
            <span className="text-sm font-semibold text-gray-700">Booking Management</span>
          </motion.div>
          
          <h1 className="text-4xl sm:text-5xl font-bold text-gray-900 mb-4">
            My Bookings
          </h1>
          <p className="text-xl text-gray-600 max-w-2xl mx-auto">
            Track and manage all your service bookings in one place
          </p>
        </motion.div>

        {/* Stats Cards */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3, duration: 0.8 }}
          className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-12"
        >
          <Card className="card card-hover text-center p-6">
            <CardContent>
              <div className="w-12 h-12 bg-primary-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <HiCalendar className="w-6 h-6 text-primary-600" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">Total Bookings</h3>
              <p className="text-3xl font-bold text-primary-600">{booking.bookings?.length || 0}</p>
            </CardContent>
          </Card>
          
          <Card className="card card-hover text-center p-6">
            <CardContent>
              <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <span className="text-2xl">✅</span>
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">Completed</h3>
              <p className="text-3xl font-bold text-green-600">
                {booking.bookings?.filter(b => b.status === "COMPLETED").length || 0}
              </p>
            </CardContent>
          </Card>
          
          <Card className="card card-hover text-center p-6">
            <CardContent>
              <div className="w-12 h-12 bg-yellow-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <span className="text-2xl">⏳</span>
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">Pending</h3>
              <p className="text-3xl font-bold text-yellow-600">
                {booking.bookings?.filter(b => b.status === "PENDING" || b.status === "BOOKED").length || 0}
              </p>
            </CardContent>
          </Card>
          
          <Card className="card card-hover text-center p-6">
            <CardContent>
              <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <span className="text-2xl">❌</span>
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">Cancelled</h3>
              <p className="text-3xl font-bold text-red-600">
                {booking.bookings?.filter(b => b.status === "CANCELLED").length || 0}
              </p>
            </CardContent>
          </Card>
        </motion.div>

        {/* Bookings Grid */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.4, duration: 0.8 }}
        >
          {booking.bookings?.length > 0 ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {booking.bookings.map((bookingItem, index) => {
                const statusStyles = getStatusStyles(bookingItem.status);
                return (
                  <motion.div
                    key={bookingItem.bookingId}
                    initial={{ opacity: 0, y: 30 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.5 + index * 0.1, duration: 0.6 }}
                    whileHover={{ scale: 1.02 }}
                  >
                    <Card className="card card-hover h-full">
                      <CardContent className="p-6">
                        {/* Header with Status */}
                        <div className="flex items-center justify-between mb-4">
                          <div className="flex items-center gap-3">
                            <div className="w-10 h-10 bg-primary-100 rounded-full flex items-center justify-center">
                              <DirectionsCarIcon className="text-primary-600" />
                            </div>
                            <div>
                              <Typography variant="h6" className="font-bold text-gray-900">
                                {bookingItem.vehicle} - {bookingItem.type}
                              </Typography>
                            </div>
                          </div>
                          <Chip
                            label={bookingItem.status}
                            className={`${statusStyles.bg} ${statusStyles.text} ${statusStyles.border} border font-medium`}
                            icon={<span className="text-sm">{getStatusIcon(bookingItem.status)}</span>}
                          />
                        </div>

                        {/* Booking Details */}
                        <div className="space-y-3 mb-6">
                          <div className="flex items-center gap-3">
                            <div className="w-8 h-8 bg-gray-100 rounded-full flex items-center justify-center">
                              <HiIdentification className="w-4 h-4 text-gray-600" />
                            </div>
                            <div>
                              <Typography variant="body2" className="text-gray-500 text-sm">
                                Booking ID
                              </Typography>
                              <Typography variant="body1" className="font-semibold text-gray-900">
                                {bookingItem.bookingNumber}
                              </Typography>
                            </div>
                          </div>

                          <div className="flex items-center gap-3">
                            <div className="w-8 h-8 bg-gray-100 rounded-full flex items-center justify-center">
                              <HiCalendar className="w-4 h-4 text-gray-600" />
                            </div>
                            <div>
                              <Typography variant="body2" className="text-gray-500 text-sm">
                                Scheduled Date
                              </Typography>
                              <Typography variant="body1" className="font-semibold text-gray-900">
                                {moment(bookingItem.scheduledAt).format("MMM DD, YYYY")}
                              </Typography>
                              <Typography variant="body2" className="text-gray-500 text-sm">
                                {moment(bookingItem.scheduledAt).format("h:mm A")}
                              </Typography>
                            </div>
                          </div>

                          <div className="flex items-center gap-3">
                            <div className="w-8 h-8 bg-gray-100 rounded-full flex items-center justify-center">
                              <HiCurrencyRupee className="w-4 h-4 text-gray-600" />
                            </div>
                            <div>
                              <Typography variant="body2" className="text-gray-500 text-sm">
                                Estimated Price
                              </Typography>
                              <Typography variant="body1" className="font-semibold text-gray-900">
                                ₹{Math.round(bookingItem.price)}
                              </Typography>
                            </div>
                          </div>
                        </div>

                        {/* Action Buttons */}
                        <div className="flex gap-2">
                          {/* Update Booking Button */}
                          {bookingItem.status !== "CANCELLED" && !bookingItem.washCreated && (
                            <Button
                              variant="outlined"
                              size="small"
                              startIcon={<HiCog className="w-4 h-4" />}
                              className="btn-secondary flex-1"
                            >
                              <UpdateBookingDialog
                                bookingId={bookingItem.bookingId}
                                vehicle={bookingItem.vehicle.toUpperCase()}
                                washType={bookingItem.type}
                                bookingAt={bookingItem.scheduledAt}
                                address={bookingItem.address}
                              />
                            </Button>
                          )}

                          {/* Cancel Button */}
                          {bookingItem.status !== "CANCELLED" &&
                            !bookingItem.washCreated &&
                            bookingItem.status !== "BOOKED" && (
                              <CancellationModal bookingId={bookingItem.bookingId} />
                            )}
                        </div>
                      </CardContent>
                    </Card>
                  </motion.div>
                );
              })}
            </div>
          ) : (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.5, duration: 0.6 }}
              className="text-center py-16"
            >
              <div className="w-24 h-24 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <HiCalendar className="w-12 h-12 text-gray-400" />
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-2">No Bookings Yet</h3>
              <p className="text-gray-600 mb-6 max-w-md mx-auto">
                You haven't made any bookings yet. Start by exploring our services and book your first appointment.
              </p>
              <Button
                variant="contained"
                size="large"
                className="btn-primary"
                onClick={() => window.location.href = "/create-booking"}
              >
                Book Now
              </Button>
            </motion.div>
          )}
        </motion.div>
      </div>
    </div>
  );
}
